import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Alert,
  Switch,
  FormControlLabel,
  InputAdornment,
  CircularProgress,
  Paper,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Close,
  Save,
  CloudUpload,
  Delete,
  Image as ImageIcon,
} from '@mui/icons-material';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../services/firebase';
import { OfferData } from '../../types/VenueAdminTypes';

/// Returns YYYY-MM-DD for the LOCAL date, not UTC.
/// toISOString() converts to UTC first which rolls the date back
/// for any timezone ahead of UTC (e.g. AEST/AEDT) during early hours.
const toLocalDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

interface EditOfferProps {
  open: boolean;
  offer: OfferData | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface OfferFormData {
  title: string;
  description: string;
  discountType: 'percentage' | 'fixed' | 'bogo' | 'free' | 'promotion';
  discountValue: number | string;
  originalPrice?: number;
  validFrom: string;
  validUntil: string;
  terms: string;
  maxRedemptions?: number;
  maxRedemptionsPerUser?: number;
  isUnlimited: boolean;
  isUnlimitedPerUser: boolean;
}

const EditOffer: React.FC<EditOfferProps> = ({ open, offer, onClose, onSuccess }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  
  // Image states
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState<boolean>(false);
  const [deleteExistingImage, setDeleteExistingImage] = useState<boolean>(false);

  const [formData, setFormData] = useState<OfferFormData>({
    title: '',
    description: '',
    discountType: 'percentage',
    discountValue: 0,
    originalPrice: undefined,
    validFrom: '',
    validUntil: '',
    terms: '',
    maxRedemptions: undefined,
    maxRedemptionsPerUser: undefined,
    isUnlimited: true,
    isUnlimitedPerUser: true,
  });

  // Load offer data when dialog opens
  useEffect(() => {
    if (offer && open) {
      const validFromDate = offer.validFrom?.toDate ? 
        toLocalDateString(offer.validFrom.toDate()) : 
        toLocalDateString(new Date(offer.validFrom));
      
      const validUntilDate = offer.validUntil?.toDate ? 
        toLocalDateString(offer.validUntil.toDate()) : 
        toLocalDateString(new Date(offer.validUntil));

      setFormData({
        title: offer.title,
        description: offer.description,
        discountType: offer.discountType,
        discountValue: offer.discountValue,
        originalPrice: offer.originalPrice || undefined,
        validFrom: validFromDate,
        validUntil: validUntilDate,
        terms: offer.terms,
        maxRedemptions: offer.maxRedemptions || undefined,
        maxRedemptionsPerUser: offer.maxRedemptionsPerUser || undefined,
        isUnlimited: !offer.maxRedemptions,
        isUnlimitedPerUser: !offer.maxRedemptionsPerUser,
      });

      // Set existing image preview
      if (offer.imageUrl) {
        setImagePreview(offer.imageUrl);
      }

      setError('');
      setSuccess('');
      setDeleteExistingImage(false);
      setSelectedImage(null);
    }
  }, [offer, open]);

  const handleInputChange = useCallback((field: keyof OfferFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleDiscountTypeChange = useCallback((newType: string) => {
    let newDiscountValue: number | string;
    
    if (newType === 'percentage' || newType === 'fixed') {
      newDiscountValue = typeof formData.discountValue === 'number' ? formData.discountValue : 0;
    } else {
      newDiscountValue = typeof formData.discountValue === 'string' ? formData.discountValue : '';
    }
    
    setFormData(prev => ({ 
      ...prev, 
      discountType: newType as 'percentage' | 'fixed' | 'bogo' | 'free' | 'promotion',
      discountValue: newDiscountValue
    }));
  }, [formData.discountValue]);

  // Image handling
  const handleImageSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setError('Image must be less than 5MB');
      return;
    }

    setSelectedImage(file);
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
    
    setDeleteExistingImage(false);
    setError('');
  }, []);

  const handleRemoveImage = useCallback(() => {
    setSelectedImage(null);
    setImagePreview(null); // clear preview — previously restored offer.imageUrl, undoing the removal visually
    setDeleteExistingImage(true);
  }, []);

  const uploadOfferImage = async (offerId: string, venueId: string, imageFile: File): Promise<{ imageUrl: string; imagePath: string }> => {
    try {
      const imagePath = `offers/${venueId}/${offerId}/image.${imageFile.name.split('.').pop()}`;
      const storageRef = ref(storage, imagePath);
      
      const metadata = {
        contentType: imageFile.type,
        customMetadata: {
          offerId: offerId,
          venueId: venueId,
          uploadedAt: new Date().toISOString(),
        }
      };
      
      await uploadBytes(storageRef, imageFile, metadata);
      const downloadURL = await getDownloadURL(storageRef);
      
      return { imageUrl: downloadURL, imagePath };
    } catch (error) {
      console.error('Error uploading image:', error);
      throw new Error('Failed to upload image');
    }
  };

  const handleSubmit = useCallback(async () => {
    if (!offer) return;

    // Validation
    if (!formData.title.trim() || !formData.description.trim()) {
      setError('Title and description are required');
      return;
    }

    if (formData.discountType === 'percentage' || formData.discountType === 'fixed') {
      if (typeof formData.discountValue !== 'number' || formData.discountValue <= 0) {
        setError('Discount value must be greater than 0');
        return;
      }
    }

    if (formData.discountType === 'fixed' && !formData.originalPrice) {
      setError('Original price is required for fixed discount');
      return;
    }

    if (!formData.validFrom || !formData.validUntil) {
      setError('Valid dates are required');
      return;
    }

    if (new Date(formData.validFrom) >= new Date(formData.validUntil)) {
      setError('Valid from date must be before valid until date');
      return;
    }

    if (!formData.terms.trim()) {
      setError('Terms and conditions are required');
      return;
    }

    if (!formData.isUnlimited && (!formData.maxRedemptions || formData.maxRedemptions <= 0) && formData.discountType !== 'promotion') {
      setError('Max redemptions must be greater than 0');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const finalPrice = formData.discountType === 'fixed' && 
                        formData.originalPrice && 
                        typeof formData.discountValue === 'number'
        ? Math.max(0, formData.originalPrice - formData.discountValue)
        : undefined;

      // Update offer data
      const newValidUntil = new Date(formData.validUntil + 'T23:59:59');
      const newValidFrom = new Date(formData.validFrom + 'T00:00:00');
      const now = new Date();
      const isNowActive = newValidFrom <= now && newValidUntil > now;

      const updateData: any = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        discountType: formData.discountType,
        discountValue: formData.discountValue,
        originalPrice: formData.originalPrice || null,
        finalPrice: finalPrice || null,
        validFrom: Timestamp.fromDate(newValidFrom),
        validUntil: Timestamp.fromDate(newValidUntil),
        terms: formData.terms.trim(),
        maxRedemptions: formData.isUnlimited ? null : formData.maxRedemptions,
        maxRedemptionsPerUser: formData.isUnlimitedPerUser ? null : formData.maxRedemptionsPerUser,
        isActive: isNowActive,
        status: isNowActive ? 'active' : newValidFrom > now ? 'scheduled' : 'expired',
        updatedAt: Timestamp.now(),
      };

      // Handle image upload/deletion
      if (selectedImage) {
        setUploadingImage(true);
        try {
          // Delete old image if exists
          if (offer.imagePath) {
            try {
              const oldImageRef = ref(storage, offer.imagePath);
              await deleteObject(oldImageRef);
            } catch (deleteError) {
              console.error('Error deleting old image:', deleteError);
            }
          }

          // Upload new image
          const { imageUrl, imagePath } = await uploadOfferImage(
            offer.id,
            offer.venueId,
            selectedImage
          );
          
          updateData.imageUrl = imageUrl;
          updateData.imagePath = imagePath;
        } catch (imageError) {
          console.error('Error uploading image:', imageError);
          setError('Failed to upload image. Other changes were saved.');
        } finally {
          setUploadingImage(false);
        }
      } else if (deleteExistingImage && offer.imagePath) {
        // Delete existing image
        try {
          const imageRef = ref(storage, offer.imagePath);
          await deleteObject(imageRef);
          updateData.imageUrl = null;
          updateData.imagePath = null;
        } catch (deleteError) {
          console.error('Error deleting image:', deleteError);
        }
      }

      // Update Firestore
      await updateDoc(doc(db, 'offers', offer.id), updateData);

      setSuccess('Offer updated successfully!');
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error updating offer:', error);
      setError('Failed to update offer. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [formData, offer, selectedImage, deleteExistingImage, onSuccess, onClose]);

  const getDiscountDisplay = () => {
    if (formData.discountType === 'percentage' && typeof formData.discountValue === 'number') {
      return `${formData.discountValue}% OFF`;
    } else if (formData.discountType === 'fixed' && typeof formData.discountValue === 'number') {
      return `$${formData.discountValue} OFF`;
    } else if (formData.discountType === 'bogo') {
      return 'BOGO';
    } else if (formData.discountType === 'free') {
      return 'FREE';
    } else if (formData.discountType === 'promotion') {
      return 'EVENT';
    }
    return '';
  };

  if (!offer) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Edit Offer</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Image Upload Section */}
          <Grid size={{ xs: 12 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                border: '2px dashed',
                borderColor: 'divider',
              }}
            >
              <Box display="flex" flexDirection="column" alignItems="center">
                <Typography variant="subtitle2" gutterBottom>
                  Offer Image (Optional)
                </Typography>

                {imagePreview ? (
                  <Box sx={{ mt: 2, position: 'relative', width: '100%', maxWidth: 300 }}>
                    <img
                      src={imagePreview}
                      alt="Offer preview"
                      style={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: 200,
                        objectFit: 'cover',
                        borderRadius: 8,
                      }}
                    />
                    <IconButton
                      onClick={handleRemoveImage}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'background.paper',
                        '&:hover': { backgroundColor: 'error.main', color: 'white' },
                      }}
                      size="small"
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{ mt: 1, textAlign: 'center' }}>
                    {deleteExistingImage && (
                      <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 1 }}>
                        Image will be removed when you save
                      </Typography>
                    )}
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<CloudUpload />}
                    >
                      Upload Image
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleImageSelect}
                      />
                    </Button>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Title */}
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Offer Title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="e.g., 50% Off Happy Hour"
              required
            />
          </Grid>

          {/* Description */}
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              multiline
              rows={3}
              placeholder="Describe your offer in detail..."
              required
            />
          </Grid>

          {/* Discount Type */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth required>
              <InputLabel>Discount Type</InputLabel>
              <Select
                value={formData.discountType}
                onChange={(e) => handleDiscountTypeChange(e.target.value)}
                label="Discount Type"
              >
                <MenuItem value="percentage">Percentage Off</MenuItem>
                <MenuItem value="fixed">Fixed Amount Off</MenuItem>
                <MenuItem value="bogo">Buy One Get One</MenuItem>
                <MenuItem value="free">Free Item/Service</MenuItem>
                <MenuItem value="promotion">Promotion / Event</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Discount Value */}
          <Grid size={{ xs: 12, sm: 6 }}>
            {formData.discountType === 'promotion' ? (
              <Alert severity="info" sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                Promotion / Event — no monetary value needed.
              </Alert>
            ) : (formData.discountType === 'percentage' || formData.discountType === 'fixed') ? (
              <TextField
                fullWidth
                label="Discount Value"
                type="number"
                value={formData.discountValue}
                onChange={(e) => handleInputChange('discountValue', parseFloat(e.target.value) || 0)}
                InputProps={{
                  startAdornment: formData.discountType === 'fixed' ? (
                    <InputAdornment position="start">$</InputAdornment>
                  ) : (
                    <InputAdornment position="start">%</InputAdornment>
                  ),
                }}
                required
              />
            ) : (
              <TextField
                fullWidth
                label="Offer Details"
                value={formData.discountValue}
                onChange={(e) => handleInputChange('discountValue', e.target.value)}
                placeholder="e.g., Buy 1 Pizza, Get 1 Free"
                required
              />
            )}
          </Grid>

          {/* Original Price (for fixed discount) */}
          {formData.discountType === 'fixed' && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Original Price"
                type="number"
                value={formData.originalPrice || ''}
                onChange={(e) => handleInputChange('originalPrice', parseFloat(e.target.value) || undefined)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                required
              />
            </Grid>
          )}

          {/* Valid From */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Valid From"
              type="date"
              value={formData.validFrom}
              onChange={(e) => handleInputChange('validFrom', e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>

          {/* Valid Until */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Valid Until"
              type="date"
              value={formData.validUntil}
              onChange={(e) => handleInputChange('validUntil', e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>

          {/* Terms */}
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Terms & Conditions"
              value={formData.terms}
              onChange={(e) => handleInputChange('terms', e.target.value)}
              multiline
              rows={3}
              placeholder="Enter terms and conditions..."
              required
            />
          </Grid>

          {/* Max Redemptions — not applicable for promotions */}
          {formData.discountType !== 'promotion' && (
            <>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isUnlimited}
                      onChange={(e) => handleInputChange('isUnlimited', e.target.checked)}
                    />
                  }
                  label="Unlimited Redemptions"
                />
                {!formData.isUnlimited && (
                  <TextField
                    fullWidth
                    label="Max Total Redemptions"
                    type="number"
                    value={formData.maxRedemptions || ''}
                    onChange={(e) => handleInputChange('maxRedemptions', parseInt(e.target.value) || undefined)}
                    sx={{ mt: 2 }}
                  />
                )}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isUnlimitedPerUser}
                      onChange={(e) => handleInputChange('isUnlimitedPerUser', e.target.checked)}
                    />
                  }
                  label="Unlimited Redemptions Per User"
                />
                {!formData.isUnlimitedPerUser && (
                  <TextField
                    fullWidth
                    label="Max Redemptions Per User"
                    type="number"
                    value={formData.maxRedemptionsPerUser || ''}
                    onChange={(e) => handleInputChange('maxRedemptionsPerUser', parseInt(e.target.value) || undefined)}
                    sx={{ mt: 2 }}
                  />
                )}
              </Grid>
            </>
          )}

          {/* Preview Chip */}
          <Grid size={{ xs: 12 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary">
                Preview:
              </Typography>
              <Chip
                label={getDiscountDisplay()}
                color="primary"
                size="small"
              />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || uploadingImage}
          startIcon={loading || uploadingImage ? <CircularProgress size={20} /> : <Save />}
        >
          {uploadingImage ? 'Uploading Image...' : loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditOffer;